import type { TimeEntry } from './types'

/** One project's share of the logged time in a range. */
export interface ProjectTotal {
  projectId: string
  /** Resolved at aggregation time; falls back to the raw id. */
  name: string
  minutes: number
  /** How many entries made up the total — a bar of one 8h entry is not the
   *  same thing as a bar of sixteen half-hours. */
  entries: number
}

/**
 * Total minutes per project across a set of entries.
 *
 * Pure, so the numbers behind the chart can be tested without a worker or a
 * browser. Sorted by minutes descending, ties broken by name so the order is
 * stable between requests rather than left to object-key order.
 */
export function totalsByProject(
  entries: readonly TimeEntry[],
  projectNames: ReadonlyMap<string, string>,
): ProjectTotal[] {
  const byProject = new Map<string, ProjectTotal>()

  for (const entry of entries) {
    const existing = byProject.get(entry.projectId)
    if (existing) {
      existing.minutes += entry.minutes
      existing.entries += 1
      continue
    }
    byProject.set(entry.projectId, {
      projectId: entry.projectId,
      // An id with no project behind it still has to appear: dropping it
      // would make the chart's total disagree with the logged total.
      name: projectNames.get(entry.projectId) ?? entry.projectId,
      minutes: entry.minutes,
      entries: 1,
    })
  }

  return [...byProject.values()].sort(
    (a, b) => b.minutes - a.minutes || a.name.localeCompare(b.name),
  )
}

/** Where items past the palette's capacity go. */
export const OTHER_KEY = '_other'

/**
 * Time on entries carrying no tag at all.
 *
 * A real row, not an absence: tags are optional, so most of the time in the
 * store may well be untagged, and leaving it out would make the breakdown
 * quietly disagree with the hours everyone actually logged.
 */
export const UNTAGGED_KEY = '_untagged'

/** Colour slots available to a chart. */
export const MAX_SERIES = 8

/** One series, with the colour slot it owns. `slot` is -1 for the folded
 *  remainder, which is drawn achromatic rather than as a ninth hue. */
export interface ReportSeries {
  key: string
  name: string
  slot: number
}

/**
 * Hand out colour slots in a given order, folding the tail past the palette.
 *
 * The caller decides the order, and that decision is the whole point: pass
 * every item that exists, in an order that does not depend on the current
 * filter, or narrowing the report will repaint the items that survive it.
 */
export function slotsFor(
  items: readonly { id: string; name: string }[],
  maxSlots: number,
  overflowName: string,
  firstSlot = 0,
): ReportSeries[] {
  const slots: ReportSeries[] = items
    .slice(0, maxSlots)
    .map((item, index) => ({ key: item.id, name: item.name, slot: index + firstSlot }))

  if (items.length > maxSlots) {
    slots.push({ key: OTHER_KEY, name: overflowName, slot: -1 })
  }

  return slots
}

/** One tag's share of the logged time in a range. */
export interface TagTotal {
  tagId: string
  name: string
  minutes: number
  entries: number
}

/** The label the untagged row carries. */
export const UNTAGGED_NAME = 'Untagged'

/**
 * Total minutes per tag across a set of entries.
 *
 * An entry with several tags counts in full under each of them, so these
 * minutes deliberately do NOT sum to the total logged — an hour tagged both
 * 'Bug Fix' and 'Refactor' is an hour of each, and splitting it in halves
 * would invent a precision nobody entered. Every caller must say so where
 * the numbers are shown.
 *
 * Sorted by minutes descending, ties broken by name, with the untagged row
 * pinned last wherever it lands: it is the remainder, not a competitor.
 */
export function totalsByTag(
  entries: readonly TimeEntry[],
  tagNames: ReadonlyMap<string, string>,
): TagTotal[] {
  const byTag = new Map<string, TagTotal>()

  const add = (tagId: string, name: string, minutes: number): void => {
    const existing = byTag.get(tagId)
    if (existing) {
      existing.minutes += minutes
      existing.entries += 1
      return
    }
    byTag.set(tagId, { tagId, name, minutes, entries: 1 })
  }

  for (const entry of entries) {
    if (!entry.tagIds.length) {
      add(UNTAGGED_KEY, UNTAGGED_NAME, entry.minutes)
      continue
    }
    for (const tagId of entry.tagIds) {
      // A tag deleted since it was logged against still shows, as its id:
      // dropping the row would lose hours out of the breakdown.
      add(tagId, tagNames.get(tagId) ?? tagId, entry.minutes)
    }
  }

  return [...byTag.values()].sort((a, b) => {
    if (a.tagId === UNTAGGED_KEY) return 1
    if (b.tagId === UNTAGGED_KEY) return -1
    return b.minutes - a.minutes || a.name.localeCompare(b.name)
  })
}

/** One person's share of a project's logged time. */
export interface PersonTotal {
  userId: string
  name: string
  minutes: number
  entries: number
}

/**
 * A pie stops being readable well before a bar chart does, so the tail folds
 * rather than growing more slices.
 */
export const MAX_SLICES = 6

/**
 * Total minutes per person across a set of entries.
 *
 * Sorted by minutes descending with ties broken by name, so the order is
 * stable between requests rather than left to object-key order.
 */
export function totalsByPerson(
  entries: readonly TimeEntry[],
  userNames: ReadonlyMap<string, string>,
): PersonTotal[] {
  const byUser = new Map<string, PersonTotal>()

  for (const entry of entries) {
    const existing = byUser.get(entry.userId)
    if (existing) {
      existing.minutes += entry.minutes
      existing.entries += 1
      continue
    }
    byUser.set(entry.userId, {
      userId: entry.userId,
      // Someone who has since been removed still logged the time; showing the
      // raw id is honest where dropping the row would silently lose hours.
      name: userNames.get(entry.userId) ?? entry.userId,
      minutes: entry.minutes,
      entries: 1,
    })
  }

  return [...byUser.values()].sort(
    (a, b) => b.minutes - a.minutes || a.name.localeCompare(b.name),
  )
}

/**
 * Keep the largest `max` rows and fold the rest into one.
 *
 * Unlike the project colour slots, these slices are ranked within a single chart
 * rather than carrying an identity across several, so folding by size is the
 * whole point: the tail is "everyone else", and its hours still have to be in
 * the total.
 */
export function foldTail<T extends { minutes: number; entries: number }>(
  rows: readonly T[],
  max: number,
  fold: (minutes: number, entries: number, count: number) => T,
): T[] {
  if (rows.length <= max) return [...rows]

  const kept = rows.slice(0, max - 1)
  const tail = rows.slice(max - 1)

  return [
    ...kept,
    fold(
      tail.reduce((n, row) => n + row.minutes, 0),
      tail.reduce((n, row) => n + row.entries, 0),
      tail.length,
    ),
  ]
}

/** One project's week: seven day cells, and what they add up to. */
export interface WeekRow {
  projectId: string
  name: string
  /** Minutes per day, Monday first. Always seven, zero where nothing was logged. */
  minutes: number[]
  total: number
}

export interface WeekGrid {
  rows: WeekRow[]
  /** Column totals, Monday first, then the grand total in `total`. */
  dayTotals: number[]
  total: number
}

/**
 * A week of entries as a project-by-day grid.
 *
 * Only projects with time in *this* week get a row, so the table is as short
 * as the week was and an empty week is visibly empty rather than a wall of
 * zeroes. Rows are ordered by name, not by hours: a row's position has to
 * stay put as the week is edited, or the cell someone is about to click
 * moves out from under them.
 *
 * Entries outside `days` are ignored rather than dropped into the nearest
 * column — a caller passing a wider range than it means to should see a
 * short grid, not a wrong one.
 */
export function weekGrid(
  entries: readonly TimeEntry[],
  projectNames: ReadonlyMap<string, string>,
  days: readonly string[],
): WeekGrid {
  const column = new Map(days.map((date, index) => [date, index]))
  const rows = new Map<string, WeekRow>()

  for (const entry of entries) {
    const index = column.get(entry.date)
    if (index === undefined) continue

    let row = rows.get(entry.projectId)
    if (!row) {
      row = {
        projectId: entry.projectId,
        // An id with no project behind it still gets a row, under the id:
        // dropping it would make the grid disagree with the hours logged.
        name: projectNames.get(entry.projectId) ?? entry.projectId,
        minutes: days.map(() => 0),
        total: 0,
      }
      rows.set(entry.projectId, row)
    }

    row.minutes[index] += entry.minutes
    row.total += entry.minutes
  }

  const ordered = [...rows.values()].sort(
    (a, b) => a.name.localeCompare(b.name) || a.projectId.localeCompare(b.projectId),
  )

  const dayTotals = days.map((_, index) =>
    ordered.reduce((n, row) => n + row.minutes[index], 0),
  )

  return {
    rows: ordered,
    dayTotals,
    total: dayTotals.reduce((n, minutes) => n + minutes, 0),
  }
}
